import { useEffect, useState } from 'react';
import { X, Loader2, Crown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import type { Team } from '@/pages/TeamsPage';

interface TenantUser {
  id: string;
  name: string;
  email: string;
  role: string;
  isActive: boolean;
}

interface Props {
  team: Team | null;
  onClose: () => void;
  onSaved: () => void;
}

// Right-side drawer used for both Create and Edit.
// Follows the same pattern used by RoleFormDrawer/UserFormDrawer —
// right-aligned sheet over a dark scrim, 480px wide.

export function TeamFormDrawer({ team, onClose, onSaved }: Props) {
  const isEdit = !!team;

  const [users, setUsers] = useState<TenantUser[]>([]);
  const [name, setName] = useState(team?.name ?? '');
  const [description, setDescription] = useState(team?.description ?? '');
  const [isActive, setIsActive] = useState(team?.isActive ?? true);
  const [leadId, setLeadId] = useState<string | null>(team?.lead?.id ?? null);
  const [memberIds, setMemberIds] = useState<Set<string>>(
    new Set(team?.members.map((m) => m.userId) ?? []),
  );
  const [saving, setSaving] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    api.get('/users')
      .then(({ data }) => {
        // /users may return a bare array OR { users: [...] } / { data: [...] }
        // depending on controller shape. Normalize + filter inactive.
        const list: TenantUser[] = Array.isArray(data)
          ? data
          : (data?.users ?? data?.data ?? []);
        setUsers(list.filter((u) => u.isActive !== false));
      })
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false));
  }, []);

  const toggleMember = (userId: string) => {
    setMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
        // If we were removing the lead, clear leadId too
        if (leadId === userId) setLeadId(null);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const promoteLead = (userId: string) => {
    setLeadId((prev) => (prev === userId ? null : userId));
    // Auto-add to members if not already
    setMemberIds((prev) => new Set(prev).add(userId));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast('error', 'Team name is required');
      return;
    }
    setSaving(true);
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      isActive,
      leadId: leadId || undefined,
      memberIds: Array.from(memberIds),
    };
    try {
      if (isEdit) {
        await api.patch(`/teams/${team!.id}`, payload);
        toast('success', 'Team updated');
      } else {
        await api.post('/teams', payload);
        toast('success', 'Team created');
      }
      onSaved();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to save team');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-lg bg-white shadow-2xl flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-bold text-slate-900">{isEdit ? 'Edit Team' : 'New Team'}</h2>
            <p className="text-xs text-slate-500 mt-0.5">Group counselors for team-based lead routing.</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Name */}
          <div className="space-y-1.5">
            <Label>Team Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. IELTS Coaching"
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label>Description (optional)</Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="What this team handles..."
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
            />
          </div>

          {/* Active */}
          <div className="flex items-center justify-between py-2 border-t border-slate-100">
            <div>
              <p className="text-sm font-medium text-slate-800">Active</p>
              <p className="text-xs text-slate-400">Inactive teams don't receive new leads</p>
            </div>
            <button
              onClick={() => setIsActive(!isActive)}
              className={`relative w-11 h-6 rounded-full transition-colors ${isActive ? 'bg-blue-600' : 'bg-slate-200'}`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${isActive ? 'left-6' : 'left-1'}`} />
            </button>
          </div>

          {/* Members list */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Members ({memberIds.size})</Label>
              <span className="text-xs text-slate-400">Click crown to set team lead</span>
            </div>
            {loadingUsers ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
              </div>
            ) : users.length === 0 ? (
              <p className="text-xs text-slate-400 py-4 text-center border border-slate-200 rounded-lg">
                No active users in this tenant. Add users first.
              </p>
            ) : (
              <div className="border border-slate-200 rounded-lg max-h-72 overflow-y-auto divide-y divide-slate-100">
                {users.map((u) => {
                  const selected = memberIds.has(u.id);
                  const isLead = leadId === u.id;
                  return (
                    <div
                      key={u.id}
                      className={`flex items-center gap-3 px-3 py-2.5 transition-colors ${
                        selected ? 'bg-blue-50/50' : 'hover:bg-slate-50'
                      }`}
                    >
                      {/* Member checkbox */}
                      <button
                        onClick={() => toggleMember(u.id)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          selected ? 'bg-blue-600 border-blue-600' : 'border-slate-300 hover:border-slate-400'
                        }`}
                      >
                        {selected && <Check className="h-3 w-3 text-white" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{u.name}</p>
                        <p className="text-xs text-slate-500 truncate">{u.email} · {u.role}</p>
                      </div>
                      {/* Lead toggle — only shown if member is selected */}
                      {selected && (
                        <button
                          onClick={() => promoteLead(u.id)}
                          className={`p-1.5 rounded-md transition-colors flex-shrink-0 ${
                            isLead
                              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                              : 'text-slate-400 hover:text-amber-600 hover:bg-amber-50'
                          }`}
                          title={isLead ? 'Team Lead' : 'Set as Team Lead'}
                        >
                          <Crown className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
            Cancel
          </button>
          <Button onClick={handleSave} disabled={saving || !name.trim()}>
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            {isEdit ? 'Save Changes' : 'Create Team'}
          </Button>
        </div>
      </div>
    </div>
  );
}
